import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@/components/ui/toast";
import { useToast } from "@/hooks/use-toast";

/**
 * Renders a list of toast notifications using a ToastProvider.
 * @example
 * Toaster()
 * <ToastProvider>
 *   {toasts.map(...)}
 *   ...
 * </ToastProvider>
 * @returns {JSX.Element} The rendered component containing the toasts.
 * @description
 *   - Uses `useToast` hook to retrieve toast notifications.
 *   - Maps each toast notification to a `Toast` component.
 *   - Includes `ToastViewport` for handling overflow of toasts.
 */
export function Toaster() {
  const { toasts } = useToast();

  return (
    <ToastProvider>
      {toasts.map(({ id, title, description, action, variant, ...props }) => {
        return (
          <Toast
            key={id}
            variant={variant}
            role={variant === "destructive" ? "alert" : "status"}
            {...props}
          >
            <div className="grid gap-1">
              {title && <ToastTitle>{title}</ToastTitle>}
              {description && <ToastDescription>{description}</ToastDescription>}
            </div>
            {action}
            <ToastClose />
          </Toast>
        );
      })}
      <ToastViewport />
    </ToastProvider>
  );
}
